import { StatsCard } from "@/components/ui/StatsCard";
import { TrendingUp, ShoppingCart } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, endOfMonth, startOfYear, endOfYear, format } from "date-fns";
import { fr } from "date-fns/locale";
import { useEffect } from "react";

export const StatsCards = () => {
  const queryClient = useQueryClient();

  const { data: monthlyStats } = useQuery({
    queryKey: ["dashboard_stats", "monthly"],
    queryFn: async () => {
      const currentDate = new Date();
      const startDate = startOfMonth(currentDate);
      const endDate = endOfMonth(currentDate);

      // Fetch validated amounts for the month
      const { data: validatedAmounts, error: validatedError } = await supabase
        .from("validated_amounts")
        .select("amount")
        .gte("counting_date", startDate.toISOString().split('T')[0])
        .lte("counting_date", endDate.toISOString().split('T')[0]);

      if (validatedError) throw validatedError;

      // Calculate total validated amount
      const totalRevenue = validatedAmounts?.reduce((sum, record) => sum + record.amount, 0) || 0;

      // Fetch transactions for count
      const { data: transactions, error } = await supabase
        .from("poultry_transactions")
        .select("*")
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      if (error) throw error;

      const sales = transactions.filter(tx => tx.transaction_type === "sale");

      return {
        totalRevenue,
        transactionCount: sales.length
      };
    }
  });

  const { data: yearlyStats } = useQuery({
    queryKey: ["dashboard_stats", "yearly"],
    queryFn: async () => {
      const currentDate = new Date();
      const startDate = startOfYear(currentDate);
      const endDate = endOfYear(currentDate);

      // Fetch validated amounts for the year
      const { data: validatedAmounts, error: validatedError } = await supabase
        .from("validated_amounts")
        .select("amount")
        .gte("counting_date", startDate.toISOString().split('T')[0])
        .lte("counting_date", endDate.toISOString().split('T')[0]);

      if (validatedError) throw validatedError;

      // Calculate total validated amount for the year
      const totalRevenue = validatedAmounts?.reduce((sum, record) => sum + record.amount, 0) || 0;

      return {
        totalRevenue
      };
    }
  });

  useEffect(() => {
    const channel = supabase
      .channel('transactions-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'poultry_transactions' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['dashboard_stats'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString()} FCFA`;
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      <StatsCard
        title="Chiffre d'affaires (Mois)"
        value={monthlyStats ? formatCurrency(monthlyStats.totalRevenue) : "--"}
        icon={TrendingUp}
        description={`${format(new Date(), 'MMMM yyyy', { locale: fr })}`}
      />
      <StatsCard
        title="Chiffre d'affaires (Année)"
        value={yearlyStats ? formatCurrency(yearlyStats.totalRevenue) : "--"}
        icon={TrendingUp}
        description={`Année ${currentYear}`}
      />
      <StatsCard
        title="Ventes"
        value={monthlyStats ? monthlyStats.transactionCount.toString() : "--"}
        icon={ShoppingCart}
        description="transactions ce mois"
      />
    </div>
  );
};